import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { API_URL } from '../utils/api';
import { KeyRound } from 'lucide-react';

const API = `${API_URL}/admin`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(`${API}/password-reset/confirm`, {
        token,
        new_password: newPassword
      });
      setMessage(response.data.message || 'Password reset successful');
      setTimeout(() => navigate('/admin/login'), 2000);
    } catch (err) {
      // Provide user-friendly error messages
      const errorDetail = err.response?.data?.detail || '';
      if (errorDetail.includes('expired') || errorDetail.includes('Invalid')) {
        setError('This reset link has expired or is invalid. Please request a new password reset.');
      } else {
        setError(errorDetail || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-container">
      <Card className="admin-login-card">
        <CardHeader>
          <div className="admin-login-icon">
            <KeyRound size={48} />
          </div>
          <CardTitle className="admin-login-title">Reset Password</CardTitle>
          <CardDescription style={{ textAlign: 'center', color: '#8b9a7e', marginTop: '8px' }}>
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="admin-login-form">
            <div className="form-group">
              <Label htmlFor="newPassword" style={{ color: '#c5bfa9' }}>New Password</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="admin-input"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="confirmPassword" style={{ color: '#c5bfa9' }}>Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className="admin-input"
              />
            </div>

            {message && (
              <div style={{ 
                padding: '12px', 
                borderRadius: '6px', 
                background: 'rgba(90, 111, 78, 0.2)', 
                border: '1px solid #5a6f4e',
                color: '#8b9a7e',
                fontSize: '0.875rem'
              }}>
                {message}
              </div>
            )}

            {error && (
              <div className="admin-login-error">
                {error}
              </div>
            )}

            <Button type="submit" className="admin-login-button" disabled={loading || !token}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>

            <div style={{ textAlign: 'center' }}>
              <Link 
                to="/admin/login" 
                style={{ 
                  color: '#5a6f4e', 
                  fontSize: '0.875rem', 
                  textDecoration: 'none',
                  fontWeight: '500'
                }}
              >
                Back to Login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
